/**
 * SF-01 «Жалоба — шаг 1» и SF-02 «шаг 2, комментарий и контекст».
 *
 * ПС §14, BS §17.12 `POST /reports`. Один диалог обслуживает все три цели
 * контракта (`user`, `message`, `media`): различаются только заголовок и
 * контекст, а правила — категория из `GET /config → report_categories`,
 * комментарий ≤ 1000 знаков и идемпотентность — общие.
 *
 * Дубль жалобы (`report_duplicate` 409) — не ошибка пользователя: жалоба
 * уже принята, и повторять её незачем.
 */
import { useState } from 'react'

import { api, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { ReportCategory } from '@shared/api/types'
import { Banner, Button, Dialog, RadioGroup, Textarea, useToast } from '@shared/ui'

import styles from './report.module.css'

/** BS §17.12: комментарий не длиннее 1000 знаков. */
const COMMENT_MAX = 1000

export const REPORT_LABELS: Record<ReportCategory, string> = {
  fake: 'Фейк или выдаёт себя за другого',
  spam: 'Спам',
  scam: 'Мошенничество',
  harassment: 'Оскорбления или домогательства',
  prohibited_content: 'Запрещённый контент',
  age_safety: 'Возраст или безопасность',
  other: 'Другое',
}

export interface ReportTarget {
  type: 'user' | 'message' | 'media'
  id: string
  /** Заголовок диалога: «Пожаловаться на профиль» / «…на сообщение». */
  title: string
  /** Кто или что — отдельной строкой: имена не склоняются, в заголовок их не вставить. */
  subject?: string | null
  /**
   * SF-02: для сообщения передаётся контекст переписки — соседние сообщения,
   * чтобы модератор видел разговор, а не одну реплику.
   */
  contextMessageIds?: readonly string[]
}

export function ReportDialog({
  target,
  categories,
  onClose,
  onSubmitted,
}: {
  target: ReportTarget | null
  categories: readonly ReportCategory[]
  onClose: () => void
  onSubmitted: (reportId: string | null) => void
}): React.ReactElement {
  const toast = useToast()
  const [category, setCategory] = useState<ReportCategory | null>(null)
  const [comment, setComment] = useState('')
  const [sending, setSending] = useState(false)

  const reset = (): void => {
    setCategory(null)
    setComment('')
  }

  const submit = async (): Promise<void> => {
    if (!target || !category) return
    setSending(true)

    try {
      const created = unwrap(
        await api.POST('/reports', {
          body: {
            target_type: target.type,
            target_id: target.id,
            category,
            ...(comment.trim() ? { comment: comment.trim().slice(0, COMMENT_MAX) } : {}),
            ...(target.contextMessageIds?.length
              ? { context_message_ids: [...target.contextMessageIds] }
              : {}),
          } as never,
          ...idempotency(uuid()),
        }),
      )

      reset()
      onSubmitted(created.id ?? null)
    } catch (error) {
      // 409: такая жалоба уже принята — говорим об этом как об успехе.
      if (error instanceof ApiError && error.code === 'report_duplicate') {
        reset()
        onSubmitted(null)
        return
      }

      toast.show('Не удалось отправить жалобу', { tone: 'danger' })
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open={target !== null}
      onClose={() => {
        reset()
        onClose()
      }}
      title={target?.title ?? 'Пожаловаться'}
      description={`${target?.subject ? `${target.subject}. ` : ''}Выберите причину. Мы не сообщаем, кто отправил жалобу.`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Отмена
          </Button>
          <Button variant="primary" disabled={!category} loading={sending} onClick={() => void submit()}>
            Отправить
          </Button>
        </>
      }
    >
      <RadioGroup
        name="report-category"
        value={category}
        onValueChange={setCategory}
        options={categories.map((value) => ({ value, label: REPORT_LABELS[value] ?? REPORT_LABELS.other }))}
      />

      <div className={styles['comment']}>
        <Textarea
          value={comment}
          onChange={(event) => setComment(event.target.value.slice(0, COMMENT_MAX))}
          placeholder="Комментарий — необязательно"
          rows={3}
          maxLength={COMMENT_MAX}
        />
        {comment.length > COMMENT_MAX - 100 ? (
          <p className={styles['counter']}>
            {comment.length} из {COMMENT_MAX}
          </p>
        ) : null}
      </div>

      {target?.type === 'message' ? (
        <Banner tone="neutral" icon="info" className={styles['note']}>
          К жалобе приложатся соседние сообщения диалога — решение принимается по разговору.
        </Banner>
      ) : (
        <Banner tone="neutral" icon="shield" className={styles['note']}>
          Если есть угроза безопасности, сразу заблокируйте пользователя.
        </Banner>
      )}
    </Dialog>
  )
}
